import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { EXCLUSION_REASONS, PIN_HISTORY_COUNT, PIN_NOTE_MAX, addDays, newId, type ExclusionReason } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { useAreas, type AreaRow } from '../../data/geo.ts'
import { EXCLUSION_PROBLEM_KEY, EXCLUSION_REASON_KEY, PIN_PROBLEM_KEY, buildCorrectPayload, courseGreens, dayPlacement, effectiveSettings, emptyExclusionForm, exclusionFormOf, exclusionPayload, exclusionProblems, exclusionsOf, historyCounts, num, offsetsOf, offsetsProblem, rangeDays, sectorText, shortDay, sortedExclusions, useFlagSettings, usePinExclusions, usePinPlacements, type ExclusionForm, type OffsetsForm, type PinExclusion, type PinPlacement, type SectorSettings } from '../../data/pinsUi.ts'
import { areaShort, longDay, useNames } from '../../data/planUi.ts'
import { fmtNum } from '../../lib/format.ts'
import { localDate } from '../../lib/time.ts'
import { BackLink, Banner, DisabledHint, Field, PendingBanner, Pill } from '../../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../../ui/hooks.ts'
import { Picto } from '../../ui/pictos.tsx'

const ISO = /^\d{4}-\d{2}-\d{2}$/

/** Planning › Emplacements des trous (M16-C, docs/M16.md "C"): the day's placement of every green, as the person on the job recorded it (a field fact,
 *  read from the mirror), what the last placements were, the recovery and exclusion areas, and the sheet to print at dawn. Whoever manages the flags
 *  (flag.manage) corrects a placement (`pin.correct@1`: a new row in the original's place, the original kept), writes an exclusion (`pin_exclusion.set@1`)
 *  and closes it (`pin_exclusion.close@1`): office intents, sent and waited for, kept on the device when the network is away (`PendingBanner`). The
 *  placements themselves are recorded from the job (the timer's card); nothing here decides anything. */
export function PinsPage() {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const navigate = useNavigate()
  const lang = i18n.language
  const search = useSearch({ strict: false }) as { d?: string }
  const today = localDate(new Date(), site.timezone)
  const date = search.d && ISO.test(search.d) ? search.d : today
  const manage = useWriteAccess('flag.manage')
  const areas = useAreas(); const placements = usePinPlacements(); const exclusions = usePinExclusions(); const settingsRow = useFlagSettings(site.site_id); const names = useNames()
  const areaById = useMemo(() => new Map((areas ?? []).map((a) => [a.id, a as AreaRow])), [areas])
  const greens = useMemo(() => courseGreens((areas ?? []) as AreaRow[], lang), [areas, lang])
  const [editing, setEditing] = useState<string | null>(null)
  const [xEditing, setXEditing] = useState<'new' | string | null>(null)
  const go = (d: string) => void navigate({ to: '/plan/pins', search: { d } })
  if (!areas || !placements || !exclusions || settingsRow === undefined || !names) return <p>{t('common.loading')}</p>
  const settings = effectiveSettings(settingsRow)
  const placed = greens.filter((g) => dayPlacement(placements, g.id, date)).length
  return (
    <section className="flex flex-col gap-4" data-testid="pins-page" data-date={date}>
      <BackLink to="/plan" search={{ d: date }}>← {t('plan.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="flag" className="size-7 text-flag" />{t('pins.title')}</h1>
      <p className="meta">{t('pins.planHelp')}</p>
      {settings.isDefault && <Banner tone="info" testId="pins-default">{t('pins.defaultWords')}</Banner>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn !min-h-11 !px-4" onClick={() => go(addDays(date, -1))} data-testid="pins-prev">{t('pins.prevDay')}</button>
        <button type="button" className="btn !min-h-11 !px-4" onClick={() => go(today)} disabled={date === today} data-testid="pins-today">{t('pins.dayToday')}</button>
        <button type="button" className="btn !min-h-11 !px-4" onClick={() => go(addDays(date, 1))} data-testid="pins-next">{t('pins.nextDay')}</button>
      </div>
      <h2 className="text-xl font-bold first-letter:uppercase" data-testid="pins-day">{longDay(date, lang)} · {placed}/{greens.length}</h2>
      <div className="flex flex-wrap gap-2">
        {/* the two routes are declared in router.tsx by Z's close (docs/m16-requests/c.md): the casts go the day they are */}
        <Link to="/plan/pins/print" search={{ d: date }} className="btn" data-testid="pins-print">{t('pins.print')}</Link>
        {manage.permitted && <Link to="/settings/flags" className="btn" data-testid="pins-settings-link">{t('pins.settingsLink')}</Link>}
      </div>
      {placed === 0 && <p className="meta" data-testid="pins-none">{t('pins.placementsNone')}</p>}
      <ul className="flex flex-col gap-3">
        {greens.map((g) => (
          <GreenRow key={g.id} green={g} areaById={areaById} lang={lang} date={date} placements={placements} exclusions={exclusions} settings={settings} names={names}
            canManage={manage.allowed} editing={editing === g.id} onEdit={() => setEditing(g.id)} onDone={() => setEditing(null)} />
        ))}
      </ul>
      <section className="flex flex-col gap-3" data-testid="pins-exclusions">
        <h2 className="text-xl font-bold">{t('pins.exclusions')}</h2>
        <p className="meta">{t('pins.exclusionsHelp')}</p>
        {manage.allowed && xEditing === null && <button type="button" className="btn btn-primary" onClick={() => setXEditing('new')} data-testid="pins-excl-new">{t('pins.exclusionNew')}</button>}
        {xEditing === 'new' && <ExclusionEditor greens={greens} areaById={areaById} lang={lang} today={today} row={null} onDone={() => setXEditing(null)} />}
        {exclusions.length === 0 && <p className="meta" data-testid="pins-excl-none">{t('pins.exclusionNone')}</p>}
        <ul className="flex flex-col gap-2">
          {sortedExclusions(exclusions, today).map((x) => (
            <ExclusionRow key={x.id} x={x} greens={greens} areaById={areaById} lang={lang} today={today} canManage={manage.allowed} editing={xEditing === x.id} onEdit={() => setXEditing(x.id)} onDone={() => setXEditing(null)} />
          ))}
        </ul>
      </section>
    </section>
  )
}

/** One green on the day: its placement (or none), the last sectors, the exclusions in force, and the correction form for whoever manages the flags. */
function GreenRow({ green, areaById, lang, date, placements, exclusions, settings, names, canManage, editing, onEdit, onDone }: {
  green: AreaRow; areaById: Map<string, AreaRow>; lang: string; date: string; placements: readonly PinPlacement[]; exclusions: readonly PinExclusion[]; settings: SectorSettings
  names: Map<string, string>; canManage: boolean; editing: boolean; onEdit: () => void; onDone: () => void
}) {
  const { t } = useTranslation()
  const day = dayPlacement(placements, green.id, date)
  const counts = [...historyCounts(placements, green.id, date)]
  const excl = exclusionsOf(exclusions, green.id, date)
  const who = day?.actor_person_id ? names.get(day.actor_person_id) : undefined
  return (
    <li className="card flex flex-col gap-1" data-testid="pins-row" data-area={green.id} data-sector={day?.sector_code ?? ''}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold">{areaShort(green, areaById, lang, t)}</span>
        {day ? <span className="text-lg font-bold" data-testid="pins-row-sector">{sectorText(day.sector_code, t)}</span> : <span className="meta" data-testid="pins-row-none">{t('pins.noPlacement')}</span>}
      </div>
      {day && (num(day.offset_front_m) !== null || num(day.offset_side_m) !== null || day.note || who || day._pending) && (
        <p className="meta flex flex-wrap items-center gap-2" data-testid="pins-row-meta">
          {num(day.offset_front_m) !== null && <span>{t('pins.offsetFront', { front: fmtNum(num(day.offset_front_m)!, lang, 1) })}</span>}
          {num(day.offset_side_m) !== null && <span>{t('pins.offsetSide', { side: fmtNum(num(day.offset_side_m)!, lang, 1) })}</span>}
          {day.note && <span>{day.note}</span>}
          {who && <span>{t('measures.by', { who })}</span>}
          {day._pending && <Pill tone="amber">{t('common.pendingSend')}</Pill>}
        </p>
      )}
      {counts.length > 0 && <p className="meta" data-testid="pins-row-history">{t('pins.history')} ({PIN_HISTORY_COUNT}) : {counts.map(([s, n]) => `${sectorText(s, t)} ×${n}`).join(', ')}</p>}
      {excl.length > 0 && <p className="meta" data-testid="pins-row-excl">{t('pins.exclusions')} : {excl.map((x) => `${x.label} (${t(EXCLUSION_REASON_KEY[x.reason])})`).join(', ')}</p>}
      {canManage && day && !day._pending && !editing && <button type="button" className="btn !min-h-11 self-start !px-4" onClick={onEdit} data-testid="pins-correct">{t('pins.correct')}</button>}
      {editing && day && <CorrectionEditor original={day} settings={settings} onDone={onDone} />}
    </li>
  )
}

/** The correction of the day's placement (pin.correct@1): the sector from the club's words, the offsets, a note. */
function CorrectionEditor({ original, settings, onDone }: { original: PinPlacement; settings: SectorSettings; onDone: () => void }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [sector, setSector] = useState(original.sector_code)
  const [f, setF] = useState<OffsetsForm>(() => offsetsOf(original))
  const problem = offsetsProblem(f)
  const words = settings.sectors.includes(sector) ? settings.sectors : [sector, ...settings.sectors]
  const submit = async () => { if (await w.run('pin.correct@1', buildCorrectPayload(id.current, original, sector, f), 'pins.corrected')) { id.current = newId(); onDone() } }
  return (
    <form className="flex flex-col gap-3 border-t pt-3 dark:border-night-line" data-testid="pins-correct-form" onSubmit={(e) => { e.preventDefault(); void submit() }}>
      <p className="meta">{t('pins.correctHelp')}</p>
      <Field label={t('pins.sheet.sector')} htmlFor={`corr-sector-${original.id}`}>
        <select id={`corr-sector-${original.id}`} className="input" value={sector} onChange={(e) => setSector(e.target.value)} data-testid="pins-correct-sector">
          {words.map((s) => <option key={s} value={s}>{sectorText(s, t)}</option>)}
        </select>
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('pins.front')} htmlFor={`corr-front-${original.id}`}><input id={`corr-front-${original.id}`} className="input" inputMode="decimal" value={f.front} onChange={(e) => setF({ ...f, front: e.target.value })} data-testid="pins-correct-front" /></Field>
        <Field label={t('pins.side')} htmlFor={`corr-side-${original.id}`}><input id={`corr-side-${original.id}`} className="input" inputMode="decimal" value={f.side} onChange={(e) => setF({ ...f, side: e.target.value })} data-testid="pins-correct-side" /></Field>
      </div>
      <Field label={t('pins.note')} hint={t('pins.noteHint')} htmlFor={`corr-note-${original.id}`}><input id={`corr-note-${original.id}`} className="input" maxLength={PIN_NOTE_MAX} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="pins-correct-note" /></Field>
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error" testId="pins-correct-error">{w.error}</Banner>}
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary" disabled={w.busy || w.pending || !!problem} data-testid="pins-correct-save">{t('pins.correctSave')}</button>
        <button type="button" className="btn" disabled={w.busy} onClick={onDone} data-testid="pins-correct-cancel">{t('common.cancel')}</button>
      </div>
      <DisabledHint show={!!problem} testId="pins-correct-hint">{problem ? t(PIN_PROBLEM_KEY[problem]) : ''}</DisabledHint>
    </form>
  )
}

/** One exclusion: its green, label, reason, dates; for whoever manages the flags, "Modifier" and "Clore aujourd'hui". */
function ExclusionRow({ x, greens, areaById, lang, today, canManage, editing, onEdit, onDone }: {
  x: PinExclusion; greens: AreaRow[]; areaById: Map<string, AreaRow>; lang: string; today: string; canManage: boolean; editing: boolean; onEdit: () => void; onDone: () => void
}) {
  const { t } = useTranslation()
  const close = useWorkshopAction()
  const r = rangeDays(x.valid_during)
  const closed = r.to !== null && r.to < today
  const dates = r.to === null ? t('pins.exclusionSince', { from: shortDay(r.from, lang) }) : t('pins.exclusionDates', { from: shortDay(r.from, lang), to: shortDay(r.to, lang) })
  return (
    <li className="card flex flex-col gap-1" data-testid="pins-excl-row" data-id={x.id} data-closed={closed ? '1' : '0'}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold">{x.label}</span>
        <Pill tone={closed ? 'gray' : 'amber'}>{t(EXCLUSION_REASON_KEY[x.reason])}</Pill>
      </div>
      <p className="meta">{areaShort(areaById.get(x.area_id), areaById, lang, t)} · {dates}{x._pending ? ` · ${t('common.pendingSend')}` : ''}</p>
      {close.pending && <PendingBanner />}
      {close.error && <Banner tone="error">{close.error}</Banner>}
      {close.ok && <Banner tone="ok" testId="pins-excl-closed">{close.ok}</Banner>}
      {canManage && !editing && !x._pending && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn !min-h-11 !px-4" disabled={close.busy} onClick={onEdit} data-testid="pins-excl-edit">{t('pins.exclusionEdit')}</button>
          {!closed && <button type="button" className="btn !min-h-11 !px-4" disabled={close.busy} onClick={() => void close.run('pin_exclusion.close@1', { exclusion_id: x.id }, 'pins.exclusionClosed')} data-testid="pins-excl-close">{t('pins.exclusionClose')}</button>}
        </div>
      )}
      {editing && <ExclusionEditor greens={greens} areaById={areaById} lang={lang} today={today} row={x} onDone={onDone} />}
    </li>
  )
}

/** The form of one exclusion: new (pin_exclusion.set@1 at revision 0 with a fresh id) or an edit (the whole row, from the revision read). */
function ExclusionEditor({ greens, areaById, lang, today, row, onDone }: { greens: AreaRow[]; areaById: Map<string, AreaRow>; lang: string; today: string; row: PinExclusion | null; onDone: () => void }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [f, setF] = useState<ExclusionForm>(() => (row ? exclusionFormOf(row) : emptyExclusionForm(today)))
  // an edit kept on the device closes when the row comes back with its new revision (the fonctions' rule, M23 item 10)
  useEffect(() => { if (w.pending && row) onDone() }, [row?.revision])                                            // eslint-disable-line react-hooks/exhaustive-deps
  const problems = exclusionProblems(f)
  const submit = async () => {
    if (await w.run('pin_exclusion.set@1', exclusionPayload(f, row ? row.id : id.current, row ? row.revision : 0), 'pins.exclusionSaved')) { id.current = newId(); onDone() }
  }
  const suffix = row ? row.id : 'new'
  return (
    <form className="card flex flex-col gap-3" data-testid="pins-excl-form" onSubmit={(e) => { e.preventDefault(); void submit() }}>
      <Field label={t('pins.exclusionGreen')} htmlFor={`x-green-${suffix}`}>
        <select id={`x-green-${suffix}`} className="input" value={f.areaId} onChange={(e) => setF({ ...f, areaId: e.target.value })} data-testid="pins-excl-green">
          <option value="">{t('pins.exclusionGreenPick')}</option>
          {greens.map((g) => <option key={g.id} value={g.id}>{areaShort(g, areaById, lang, t)}</option>)}
        </select>
      </Field>
      <Field label={t('pins.exclusionLabel')} htmlFor={`x-label-${suffix}`}>
        <input id={`x-label-${suffix}`} className="input" maxLength={80} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="pins-excl-label" />
      </Field>
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-semibold">{t('pins.exclusionReason')}</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {EXCLUSION_REASONS.map((r: ExclusionReason) => (
            <button key={r} type="button" aria-pressed={f.reason === r} onClick={() => setF({ ...f, reason: r })} className={`btn ${f.reason === r ? 'btn-primary' : ''}`} data-testid={`pins-excl-reason-${r}`}>{t(EXCLUSION_REASON_KEY[r])}</button>
          ))}
        </div>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('pins.exclusionFrom')} htmlFor={`x-from-${suffix}`}>
          <input id={`x-from-${suffix}`} type="date" className="input" required value={f.from} onChange={(e) => setF({ ...f, from: e.target.value, to: f.to && f.to < e.target.value ? e.target.value : f.to })} data-testid="pins-excl-from" />
        </Field>
        <Field label={t('pins.exclusionTo')} hint={t('pins.exclusionToHint')} htmlFor={`x-to-${suffix}`}>
          <input id={`x-to-${suffix}`} type="date" className="input" min={f.from} value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="pins-excl-to" />
        </Field>
      </div>
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error" testId="pins-excl-error">{w.error}</Banner>}
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary" disabled={w.busy || w.pending || problems.length > 0} data-testid="pins-excl-save">{t('pins.exclusionSave')}</button>
        <button type="button" className="btn" disabled={w.busy} onClick={onDone} data-testid="pins-excl-cancel">{t('common.cancel')}</button>
      </div>
      <DisabledHint show={problems.length > 0} testId="pins-excl-hint">{t(EXCLUSION_PROBLEM_KEY[problems[0] ?? 'label'])}</DisabledHint>
    </form>
  )
}
